import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { enableVisualEditing } from "@sanity/visual-editing";
import { client, useLiveMode } from "../lib/sanity";

/**
 * Renders nothing. When the site is loaded inside the Sanity Presentation tool
 * iframe, it turns on click-to-edit overlays and keeps navigation in sync
 * between the Studio and the previewed page. It's a no-op everywhere else
 * (normal visitors are not inside an iframe).
 */
export default function VisualEditing() {
  const navigate = useNavigate();
  const location = useLocation();
  const reportRef = useRef<((u: { type: string; url: string }) => void) | null>(
    null
  );

  // Stream live (draft) content from the Studio over the comlink, so edits show
  // up in the preview as they're typed. This component is only mounted inside
  // the Presentation iframe (see App.tsx), so it never runs on the public site.
  useLiveMode({ client });

  useEffect(() => {
    if (typeof window === "undefined" || window.self === window.top) return;

    const disable = enableVisualEditing({
      history: {
        // The Studio gives us a callback to report the iframe's current URL.
        subscribe: (cb: any) => {
          reportRef.current = cb;
          return () => {
            reportRef.current = null;
          };
        },
        // The Studio asks the iframe to navigate.
        update: (update: any) => {
          if (update.type === "push") navigate(update.url);
          else if (update.type === "replace")
            navigate(update.url, { replace: true });
          else if (update.type === "pop") navigate(-1);
        },
      },
    });

    return () => disable();
  }, [navigate]);

  // Report client-side navigation back to the Studio so its address bar follows.
  useEffect(() => {
    reportRef.current?.({
      type: "push",
      url: location.pathname + location.search,
    });
  }, [location]);

  return null;
}
